import { Link } from 'react-router-dom'
import './HowItWorks.css'

// The Hubthrill Ecosystem modules
const ecosystemModules = [
  {
    id: 'studio',
    name: 'Studio',
    label: 'Build',
    description: 'AI-powered app builder',
    color: 'studio',
    position: 'top',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 19l7-7 3 3-7 7-3-3z"/>
        <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
        <path d="M2 2l7.586 7.586"/>
      </svg>
    ),
  },
  {
    id: 'chekkouts',
    name: 'Chekkouts',
    label: 'Payments',
    description: 'Accept & process payments',
    color: 'payments',
    position: 'top-right',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="1" y="4" width="22" height="16" rx="2"/>
        <line x1="1" y1="10" x2="23" y2="10"/>
      </svg>
    ),
  },
  {
    id: 'keeply',
    name: 'Keeply',
    label: 'Accounting',
    description: 'Track finances & reports',
    color: 'accounting',
    position: 'bottom-right',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/>
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
      </svg>
    ),
  },
  {
    id: 'relay',
    name: 'Relay',
    label: 'Support',
    description: 'Live chat & help desk',
    color: 'relay',
    position: 'bottom',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    ),
  },
  {
    id: 'people',
    name: 'People',
    label: 'HR',
    description: 'Team & payroll management',
    color: 'people',
    position: 'bottom-left',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
  },
  {
    id: 'namelyft',
    name: 'Namelyft',
    label: 'Infrastructure',
    description: 'Hosting & deployment',
    color: 'infrastructure',
    position: 'top-left',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="2" width="20" height="8" rx="2"/>
        <rect x="2" y="14" width="20" height="8" rx="2"/>
        <circle cx="6" cy="6" r="1" fill="currentColor"/>
        <circle cx="6" cy="18" r="1" fill="currentColor"/>
      </svg>
    ),
  },
]

function HowItWorks() {
  return (
    <section className="how-it-works section" id="platform">
      <div className="container">
        <div className="section-header">
          <span className="section-label">The Hubthrill Ecosystem</span>
          <h2 className="section-title">
            One platform. <span className="gradient-text">Six powerful modules.</span>
          </h2>
          <p className="section-subtitle">
            When you create a Hubthrill account, you unlock access to an entire ecosystem 
            of business tools — all sharing one identity, one dashboard, and seamless 
            data flow between them.
          </p>
        </div>

        {/* Ecosystem Visualization */}
        <div className="ecosystem-visualization">
          {/* Central Hub */}
          <div className="ecosystem-hub">
            <div className="hub-pulse"></div>
            <div className="hub-ring"></div>
            <div className="hub-core">
              <svg width="48" height="48" viewBox="0 0 40 40" fill="none">
                <rect width="40" height="40" rx="10" fill="url(#hubGradient)"/>
                <path d="M12 20h16M20 12v16" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                <defs>
                  <linearGradient id="hubGradient" x1="0" y1="0" x2="40" y2="40">
                    <stop stopColor="#7C3AED"/>
                    <stop offset="1" stopColor="#2563EB"/>
                  </linearGradient>
                </defs>
              </svg>
              <span className="hub-label">Hubthrill</span>
            </div>
          </div>

          {/* Module Nodes */}
          <div className="ecosystem-modules">
            {ecosystemModules.map((module, index) => (
              <Link 
                key={module.id}
                to={`/modules/${module.id}`}
                className={`ecosystem-node ecosystem-node-${module.position} ecosystem-node-${module.color}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="node-connector"></div>
                <div className="node-content">
                  <div className="node-icon">{module.icon}</div>
                  <span className="node-name">{module.name}</span>
                  <span className="node-description">{module.description}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* How It Works Steps */}
        <div className="flow-features">
          <div className="feature-card">
            <div className="feature-number">01</div>
            <h3 className="feature-title">Create your account</h3>
            <p className="feature-description">
              Sign up once with your email. Your Hubthrill ID gives you instant access 
              to the entire ecosystem — no multiple registrations ever.
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-number">02</div>
            <h3 className="feature-title">Activate modules</h3>
            <p className="feature-description">
              Turn on Studio to build, Chekkouts for payments, Keeply for accounting — 
              each module activates with one click from your dashboard.
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-number">03</div>
            <h3 className="feature-title">Everything connects</h3>
            <p className="feature-description">
              Data flows automatically. Payments from Chekkouts sync to Keeply. 
              Chat from Relay links to customer orders. It all just works.
            </p>
          </div>
        </div>

        {/* Data Flow Highlight */}
        <div className="ecosystem-highlight">
          <div className="highlight-content">
            <h3>Seamless data flow across your business</h3>
            <p>
              When a customer pays through Chekkouts, the transaction automatically appears 
              in Keeply. When they start a chat in Relay, your team sees their full purchase 
              history. People syncs payroll costs to your accounting. Every module works 
              together as one unified system.
            </p>
            <Link to="/signup" className="btn btn-primary">
              Start building your ecosystem
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}

export default HowItWorks
